import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the category master context
const CategoryMasterContext = createContext();

// Custom hook to use the category master context
export const useCategoryMaster = () => useContext(CategoryMasterContext);

// Provider component to wrap the app
export const CategoryMasterProvider = ({ children }) => {
  const [categories, setCategory] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/categories"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCategories = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const { data: paginationData } = response.data;
      const { data, current_page, last_page, per_page, total } = paginationData;

      setCategory(data || []);

      setPagination({
        currentPage: current_page,
        totalPages: last_page,
        totalItems: total,
        perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching categories master:", error);
      setError(error.response?.data?.message || "Failed to fetch categories.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch category master from the API on load
  useEffect(() => {
    fetchCategories();
  }, [API_URL, token]);

  // Add a new category
  const addCategory = async (newCategory) => {
    try {
      const response = await axios.post(API_URL, newCategory, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      // setCategory((prev) => [...prev, response.data?.data]);
      setCategory((prev) => [response.data?.data, ...prev]);
      
    } catch (error) {
      console.error("Error adding category:", error);
    }
  };

  // Update an existing category
  const updateCategory = async (updatedCategory) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedCategory.id}`, updatedCategory, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setCategory((prev) =>
        prev.map((category) =>
          category.id === updatedCategory.id ? { ...category, ...response.data?.data } : category
        )
      );
    } catch (error) {
      console.error("Error updating category master:", error);
    }
  };

  // Delete a category
  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setCategory((prev) => prev.filter((category) => category.id !== id));
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      categories,
      fetchCategories,
      addCategory,
      updateCategory,
      deleteCategory,
    }),
    [categories, isLoading, pagination] // Recalculate the value only when `categories` changes
  );

  return <CategoryMasterContext.Provider value={value}>{children}</CategoryMasterContext.Provider>;
};
